import { computed, type ComputedRef, type Ref } from 'vue'
import { translate } from 'src/layouts/core-layout/_functions/translate'
import useAuthLinks from 'src/layouts/core-layout/components/CoreHeader/components/mobile/composables/useAuthLinks'
import type { SideMenuItem } from 'src/layouts/core-layout/components/CoreHeader/components/mobile/SideMenu/SideMenu.types'
import type { ContextStore } from 'src/layouts/core-layout/types'
import { useContextStore } from 'src/store'

const MEDTOCHKA_ROUTES = {
  home: '',
  profile: 'profile',
  appointments: 'appointments',
  rates: 'rates',
  favourites: 'favourites',
  medcard: 'medcard',
}

type UseSideMenuItemsOptions = {
  town: Ref<string>
  expertUrl: Ref<string>
  personalAreaUrl: Ref<string>
  medcardStatusText: ComputedRef<string | null>
}

export default function useSideMenuItems(options: UseSideMenuItemsOptions) {
  const { context }: ContextStore = useContextStore()
  const {
    town,
    expertUrl,
    personalAreaUrl,
    medcardStatusText,
  } = options
  const {
    patientAuthPath,
    profileAuthPath,
    buildLoginPath,
    loginRelocation,
  } = useAuthLinks()

  const medtochkaUrl = context.global.sites.siteNameAppMT || ''
  const isRealPatient = Boolean(context.request.user.patient_profile)
  const isAnonymous = !context.request.user.is_authenticated
  const isYDoc = !context.request.country.is_russia

  function getMedtochkaSectionUrl(route: string) {
    return `${medtochkaUrl}${route}`
  }

  function navigateTo(url: string) {
    if (typeof window === 'undefined' || !url) {
      return
    }

    window.location.href = url
  }

  const primaryUserItem = computed<SideMenuItem | null>(() => {
    if (isRealPatient) {
      return {
        key: 'profile',
        icon: 'ui-icon-user-only',
        label: translate('Профиль'),
        href: getMedtochkaSectionUrl(MEDTOCHKA_ROUTES.profile),
      }
    }

    if (!isAnonymous) {
      return {
        key: 'personal-area',
        icon: 'ui-icon-doctor',
        label: translate('Личный кабинет'),
        href: personalAreaUrl.value,
      }
    }

    return null
  })

  const townTitle = computed(() => (
    town.value ? town.value : translate('Город не указан')
  ))
  const townTitleClass = computed(() => (town.value ? 'text-body-1 text--text' : 'text-body-1 text-info--text'))

  const guestAuthItems = computed<SideMenuItem[]>(() => ([
    {
      key: 'patient-login',
      qa: 'patient_login',
      icon: 'ui-icon-user-only',
      label: translate('Войти как пациент'),
      onClick: () => loginRelocation(buildLoginPath(patientAuthPath.value)),
    },
    {
      key: 'clinic-login',
      qa: 'clinic_login',
      icon: 'ui-icon-doctor',
      label: translate('Войти как врач/клиника'),
      onClick: () => loginRelocation(buildLoginPath(profileAuthPath.value)),
    },
  ]))

  const expertItems = computed<SideMenuItem[]>(() => ([
    {
      key: 'expert-lpu',
      qa: 'lpu_expert_link',
      icon: 'ui-icon-clinic',
      label: translate('Клиникам'),
      onClick: () => navigateTo(expertUrl.value),
    },
    {
      key: 'expert-doctor',
      qa: 'doctor_expert_link',
      icon: 'ui-icon-doctor-2',
      label: translate('Врачам'),
      onClick: () => navigateTo(`${expertUrl.value}vrachu/`),
    },
  ]))

  const patientCabinetItems = computed<SideMenuItem[]>(() => ([
    {
      key: 'cabinet-home',
      icon: 'ui-icon-home-MedLock',
      label: translate('Главная кабинета'),
      href: getMedtochkaSectionUrl(MEDTOCHKA_ROUTES.home),
    },
    {
      key: 'cabinet-appointments',
      icon: 'ui-icon-calendar-date-and-clock',
      label: translate('Записи на приём'),
      href: getMedtochkaSectionUrl(MEDTOCHKA_ROUTES.appointments),
    },
    {
      key: 'cabinet-rates',
      icon: 'ui-icon-Reviews',
      label: translate('Отзывы'),
      href: getMedtochkaSectionUrl(MEDTOCHKA_ROUTES.rates),
    },
    {
      key: 'cabinet-favourites',
      icon: 'ui-icon-heart',
      label: translate('Избранное'),
      href: getMedtochkaSectionUrl(MEDTOCHKA_ROUTES.favourites),
    },
    {
      key: 'cabinet-medcard',
      icon: 'ui-icon-med-book',
      label: translate('Медкарта'),
      href: getMedtochkaSectionUrl(MEDTOCHKA_ROUTES.medcard),
      subtitle: medcardStatusText.value,
    },
  ]))

  return {
    isRealPatient,
    isAnonymous,
    isYDoc,
    primaryUserItem,
    townTitle,
    townTitleClass,
    guestAuthItems,
    expertItems,
    patientCabinetItems,
  }
}
